import React, { FC } from 'react';
import styled from 'styled-components';
import { Clinic } from '../../common/model/dto/clinic';
import { ClinicInfoDataUtils } from '../../common/utils/services/clinic-info-data-utils';
import breakpoints from '../../design-system/breakpoints';
import Colours from '../../design-system/colours';
import OvTable from '../UI/molecules/OvTable';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { useTranslation } from 'react-i18next';
import { CLINIC_PROFILE_IMAGES_PATH } from '../../api/firebase-storage';
import OvEditableAvatar from '../UI/molecules/OvEditableAvatar';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import FilterableClinicLocationList from './FilterableClinicLocationList';
import { Resource } from '../../common/model/type/resource.enum';
import useAuthorized from '../../hooks/use-authorized';
import { UseMutationResult } from '@tanstack/react-query';

interface ClinicInfoProps {
  selectedClinic: Clinic;
  updateClinicMutation: UseMutationResult<
    string,
    Error,
    {
      clinicId: string;
      updatedProperties: Partial<Clinic>;
    },
    unknown
  >;
}

const ClinicInfo: FC<ClinicInfoProps> = ({
  selectedClinic,
  updateClinicMutation,
}) => {
  const { t } = useTranslation();

  const applicationOperations = useAuthorized(Resource.Application);
  const clinicOperations = useAuthorized(Resource.Clinic);

  const clinicUpdateAllowed =
    clinicOperations.update || applicationOperations.supervise;

  const handleClinicUpdate = (updatedProperties: Partial<Clinic>) => {
    updateClinicMutation.mutate({
      clinicId: selectedClinic.id as string,
      updatedProperties,
    });
  };

  return (
    <>
      <OvRecordDetailsSection title={t('clinicDetails.tabs.clinicInfo')}>
        <InfoLayout>
          <OvEditableAvatar
            entity={selectedClinic}
            handleUpdate={handleClinicUpdate}
            imageBasePath={CLINIC_PROFILE_IMAGES_PATH}
            dialogTitle={t('clinicDialogs.clinicImageUploadTitle')}
            defaultImageComponent={<ProfileImage />}
          />
          <TableWrapper>
            <OvTable
              data={ClinicInfoDataUtils.mapClinicInfoToTableDataFormat(
                selectedClinic
              )}
              theme="Light"
              onSaveRowValue={handleClinicUpdate}
              editable={clinicUpdateAllowed}
            />
          </TableWrapper>
        </InfoLayout>
      </OvRecordDetailsSection>

      <SitesArea>
        <FilterableClinicLocationList selectedClinic={selectedClinic} />
      </SitesArea>
    </>
  );
};

export default ClinicInfo;

const InfoLayout = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: center;
  }
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  align-self: flex-start;
  min-width: 0;
  width: 100%;
`;

const SitesArea = styled.div`
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const ProfileImage = styled(LocalHospitalIcon)`
  && {
    width: 12rem !important;
    height: 12rem !important;
    border: 0.375rem solid;
    border-radius: 50%;
    margin-bottom: 0.25rem;
    padding: 0.75rem;
  }
`;
